import { isSameDay, isWithinInterval } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'

import { PickersDay, PickersDayProps } from '@mui/x-date-pickers'
import { DatePickerCustomComponent } from '../'
import { useStyles } from './style'

interface Props {
  startDate: Date | null
  endDate: Date | null
  onChangeStartDate: (date: Date | null) => void
  onChangeEndDate: (date: Date | null) => void
}

export const DateRangePickerComponent = ({
  startDate,
  endDate,
  onChangeStartDate,
  onChangeEndDate,
}: Props) => {
  const { t } = useTranslation()

  const { classes, cx } = useStyles()

  const newStartDate = startDate ? new Date(startDate) : null

  const newEndDate = endDate ? new Date(endDate) : null

  const isWithinRange = (date: Date | null): boolean => {
    return newStartDate !== null && newEndDate !== null && date !== null
      ? isWithinInterval(date, { start: newStartDate, end: newEndDate })
      : false
  }

  const getPickersDayProps = (disabled: boolean) => ({
    borderRadius: 0,
    backgroundColor: 'transparent',
    opacity: disabled ? 0.5 : 1,
  })

  const getRenderDay = (
    calendar: 'start' | 'end',
    params: PickersDayProps<Date>,
  ) => {
    const { day } = params
    const dayIsBetween = isWithinRange(day)
    const disabled =
      calendar === 'start'
        ? newEndDate && day > newEndDate
        : newStartDate && day < newStartDate
    const isFirstDay = isSameDay(day as Date, startDate as Date)
    const isLastDay = isSameDay(day as Date, endDate as Date)
    const wrapperClassName = cx({
      [classes.highlight]: dayIsBetween,
      [classes.firstHighlight]: isFirstDay,
      [classes.endHighlight]: isLastDay,
    })

    return (
      <PickersDay
        {...params}
        className={wrapperClassName}
        disabled={disabled}
        sx={getPickersDayProps(disabled)}
      />
    )
  }

  return (
    <Box display="flex" alignItems="center" gap={1} width={285}>
      <DatePickerCustomComponent
        label={t('labels.from')}
        value={newStartDate}
        onChange={onChangeStartDate}
        dayOfWeekFormatter={(day) => day}
        maxDate={newEndDate || undefined}
        slots={{
          day: (params: PickersDayProps<Date>) => getRenderDay('start', params),
        }}
        slotProps={{
          textField: { required: true },
        }}
      />
      <DatePickerCustomComponent
        label={t('labels.to')}
        value={newEndDate}
        minDate={newStartDate || undefined}
        onChange={onChangeEndDate}
        dayOfWeekFormatter={(day) => day}
        slots={{
          day: (params: PickersDayProps<Date>) => getRenderDay('end', params),
        }}
        slotProps={{
          textField: { required: true },
        }}
      />
    </Box>
  )
}
